import React, { useMemo, useContext } from "react";
import PropTypes from "prop-types";

import { Button, Field, Control, Label, Input, Column } from "rbx";

import {
  BooleanInput,
  SystemCodeSelect,
  StateSelect,
  DateInput
} from "../inputs";
import Steps from "../Steps";

import { ModalContext } from "../ModalProvider";

const VehicleForm = ({
  inputs,
  onChange,
  onSave,
  checkExistence,
  loadingExistence,
  numberError
}) => {
  const { setModalOpen } = useContext(ModalContext);

  const VEHICLE_STEPS = useMemo(
    () => [
      {
        title: "Profile",
        content: (
          <Column.Group>
            <Column>
              <Field>
                <Column.Group>
                  <Column>
                    <Label>Vehicle #</Label>
                    <Control loading={loadingExistence}>
                      <Input
                        required
                        autoComplete="new"
                        color={numberError ? "danger" : ""}
                        name="VehicleNumber"
                        type="text"
                        value={inputs.VehicleNumber}
                        onBlur={checkExistence}
                        onChange={onChange}
                      />
                    </Control>
                  </Column>
                  <Column>
                    <Control expanded>
                      <Label>Description</Label>
                      <Input
                        autoComplete="new"
                        name="Description"
                        type="text"
                        value={inputs.Description}
                        onChange={onChange}
                      />
                    </Control>
                  </Column>
                </Column.Group>
                <Column.Group>
                  <Column size={6}>
                    <Control>
                      <Label>ELD ID</Label>
                      <Input
                        autocomplete="new"
                        name="ELDVehicleId"
                        type="text"
                        value={inputs?.ELDVehicleId}
                        onChange={onChange}
                      />
                    </Control>
                  </Column>
                  <Column size={6}>
                    <Control>
                      <Label>Company Owned</Label>
                      <BooleanInput
                        name="CompanyOwned"
                        value={inputs.CompanyOwned}
                        onChange={onChange}
                      />
                    </Control>
                  </Column>
                </Column.Group>
              </Field>
              <Field kind="group">
                <Control expanded>
                  <SystemCodeSelect
                    required
                    icon="truck"
                    label="Vehicle Type"
                    lookup={{ Category: "Fleet", Code: "VehicleType" }}
                    name="VehicleTypeRecIdRef"
                    value={inputs.VehicleTypeRecIdRef}
                    onChange={onChange}
                  />
                </Control>
                <Control expanded>
                  <SystemCodeSelect
                    readOnly
                    icon="truck"
                    label="Status"
                    lookup={{ Category: "Fleet", Code: "VehicleStatus" }}
                    name="StatusRecIdRef"
                    value={inputs.StatusRecIdRef}
                    onChange={onChange}
                  />
                </Control>
                <Control expanded>
                  <SystemCodeSelect
                    icon="truck"
                    label="Category"
                    lookup={{ Category: "Fleet", Code: "VehicleCategory" }}
                    name="CategoryRecIdRef"
                    value={inputs.CategoryRecIdRef}
                    onChange={onChange}
                  />
                </Control>
              </Field>
            </Column>
          </Column.Group>
        )
      },
      {
        title: "Make & Model",
        content: (
          <React.Fragment>
            <Field kind="group">
              <Control expanded>
                <Label>Year</Label>
                <Input
                  autoComplete="new"
                  maxLength="4"
                  name="Year"
                  type="text"
                  value={inputs.Year}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control expanded>
                <Label>Make</Label>
                <Input
                  autoComplete="new"
                  name="Make"
                  type="text"
                  value={inputs.Make}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control expanded>
                <Label>Model</Label>
                <Input
                  autoComplete="new"
                  name="Model"
                  type="text"
                  value={inputs.Model}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control expanded>
                <Label>Serial #</Label>
                <Input
                  autoComplete="new"
                  name="SerialNumber"
                  type="text"
                  value={inputs.SerialNumber}
                  onChange={onChange}
                />
              </Control>
            </Field>
          </React.Fragment>
        )
      },
      {
        title: "Details",
        content: (
          <React.Fragment>
            <Field kind="group">
              <Control expanded>
                <Label>Engine</Label>
                <Input
                  autoComplete="new"
                  name="Engine"
                  type="text"
                  value={inputs.Engine}
                  onChange={onChange}
                />
              </Control>
              <Control expanded>
                <Label>Axles</Label>
                <Input
                  autoComplete="off"
                  min={0}
                  name="Axles"
                  type="number"
                  value={inputs.Axles}
                  onChange={onChange}
                />
              </Control>
              <Control expanded>
                <SystemCodeSelect
                  icon="truck"
                  label="Transmission"
                  lookup={{ Category: "Fleet", Code: "VehicleTransmission" }}
                  name="TransmissionRecIdRef"
                  value={inputs.TransmissionRecIdRef}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field kind="group">
              <Control expanded>
                <SystemCodeSelect
                  icon="truck"
                  label="Rear"
                  lookup={{ Category: "Fleet", Code: "VehicleRear" }}
                  name="RearRecIdRef"
                  value={inputs.RearRecIdRef}
                  onChange={onChange}
                />
              </Control>
              <Control expanded>
                <SystemCodeSelect
                  icon="truck"
                  label="Body"
                  lookup={{ Category: "Fleet", Code: "VehicleBody" }}
                  name="BodyRecIdRef"
                  value={inputs.BodyRecIdRef}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field kind="group">
              <Control expanded>
                <Label># of Tanks</Label>
                <Input
                  autoComplete="off"
                  min={0}
                  name="NumDieselTanks"
                  type="number"
                  value={inputs.NumDieselTanks}
                  onChange={onChange}
                />
              </Control>
              <Control expanded>
                <SystemCodeSelect
                  icon="truck"
                  label="Tire Size"
                  lookup={{ Category: "Fleet", Code: "VehicleTireSize" }}
                  name="TireSizeRecIdRef"
                  value={inputs.TireSizeRecIdRef}
                  onChange={onChange}
                />
              </Control>
              <Control expanded>
                <SystemCodeSelect
                  icon="truck"
                  label="Wheel Size"
                  lookup={{ Category: "Fleet", Code: "VehicleWheelSize" }}
                  name="WheelSizeRecIdRef"
                  value={inputs.WheelSizeRecIdRef}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field kind="group">
              <Control expanded>
                <Label>Max. Cold</Label>
                <Field kind="addons">
                  <Control expanded>
                    <Input
                      autoComplete="off"
                      min={0}
                      name="MaxCold"
                      type="number"
                      value={inputs.MaxCold}
                      onChange={onChange}
                    />
                  </Control>
                  <Control>
                    <Button static>&deg; C</Button>
                  </Control>
                </Field>
              </Control>
              <Control expanded>
                <Label>Max. Warm</Label>
                <Field kind="addons">
                  <Control expanded>
                    <Input
                      autoComplete="off"
                      min={0}
                      name="MaxWarm"
                      type="number"
                      value={inputs.MaxWarm}
                      onChange={onChange}
                    />
                  </Control>
                  <Control>
                    <Button static>&deg; C</Button>
                  </Control>
                </Field>
              </Control>
              <Control expanded>
                <Label>Odometer</Label>
                <Field kind="addons">
                  <Control expanded>
                    <Input
                      autoComplete="off"
                      min={0}
                      name="Odometer"
                      type="number"
                      value={inputs.Odometer}
                      onChange={onChange}
                    />
                  </Control>
                </Field>
              </Control>
            </Field>
          </React.Fragment>
        )
      },
      {
        title: "Registration",
        content: (
          <React.Fragment>
            <Field>
              <Control>
                <Label>Plate #</Label>
                <Input
                  autoComplete="new"
                  name="PlateNumber"
                  type="text"
                  value={inputs.PlateNumber}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Registration #</Label>
                <Input
                  autoComplete="new"
                  name="RegistrationNumber"
                  type="text"
                  value={inputs.RegistrationNumber}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Registration State</Label>
                <StateSelect
                  icon="globe"
                  name="RegistrationState"
                  value={inputs.RegistrationState}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Registration Exp. Date</Label>
                <DateInput
                  name="RegistrationExpirationDate"
                  type="date"
                  value={inputs.RegistrationExpirationDate}
                  onChange={onChange}
                />
              </Control>
            </Field>
          </React.Fragment>
        )
      },
      {
        title: "Weights",
        content: (
          <React.Fragment>
            <Field>
              <Control>
                <Label>Overweight Permit</Label>
                <Input
                  autoComplete="new"
                  name="OverWeightPermit"
                  type="text"
                  value={inputs.OverWeightPermit}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Unladen</Label>
                <Input
                  autoComplete="off"
                  min={0}
                  name="Unladen"
                  type="number"
                  value={inputs.Unladen}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Payload</Label>
                <Input
                  autoComplete="off"
                  min={0}
                  name="Payload"
                  type="number"
                  value={inputs.Payload}
                  onChange={onChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <Label>Trycock Net</Label>
                <Input
                  autoComplete="off"
                  min={0}
                  name="TrycockNet"
                  type="number"
                  value={inputs.TrycockNet}
                  onChange={onChange}
                />
              </Control>
            </Field>
          </React.Fragment>
        )
      },
      {
        title: "Confirm",
        content: (
          <div className="confirm-container">
            <Button color="success" size="large" type="button" onClick={onSave}>
              Create Vehicle
            </Button>
          </div>
        )
      }
    ],
    [loadingExistence, inputs, onChange, numberError, checkExistence, onSave]
  );
  return (
    <Steps
      defaultStep={0}
      numberError={numberError}
      steps={VEHICLE_STEPS}
      onCancel={() => setModalOpen(false, "")}
      onCompleteLastStep={onSave}
    />
  );
};

VehicleForm.propTypes = {
  inputs: PropTypes.object,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  checkExistence: PropTypes.func,
  loadingExistence: PropTypes.bool,
  numberError: PropTypes.bool
};
VehicleForm.defaultProps = {
  inputs: {}
};
export default VehicleForm;
